import { useCallback, useState } from "react";
import { toast } from "sonner";

import { KeyFactsAPI, keyFactErrorText } from "../api";
import type { KeyFact, KeyFactActionBody } from "../types";

export type KeyFactActions = {
  /** `"<key>:<action>"` while that action is in flight. */
  busy: string | null;
  act: (key: string, body: KeyFactActionBody) => Promise<boolean>;
  fileAsExhibit: (key: string, sourceId: string) => Promise<void>;
  cancelAnalysis: (key: string) => Promise<void>;
};

/**
 * The attorney's actions on key facts. Each puts the returned key fact into
 * the cache (the live event confirms it moments later) and toasts the
 * server's own message.
 */
export const useKeyFactActions = (
  individualId: string,
  put: (fact: KeyFact) => void,
  refetch: () => void
): KeyFactActions => {
  const [busy, setBusy] = useState<string | null>(null);

  const act = useCallback(
    async (key: string, body: KeyFactActionBody) => {
      setBusy(`${key}:${body.action}`);
      try {
        const result = await KeyFactsAPI.act(individualId, key, body);
        if (result.fact) put(result.fact);
        if (result.recomputed?.length) refetch();
        if (result.message) toast.success(result.message);
        return true;
      } catch (error) {
        toast.error(keyFactErrorText(error));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [individualId, put, refetch]
  );

  const fileAsExhibit = useCallback(
    async (key: string, sourceId: string) => {
      setBusy(`${key}:exhibit:${sourceId}`);
      try {
        const result = await KeyFactsAPI.fileSourceAsExhibit(
          individualId,
          key,
          sourceId
        );
        if (result.fact) put(result.fact);
        toast.success(result.message || "Added to the case files.");
      } catch (error) {
        toast.error(keyFactErrorText(error));
      } finally {
        setBusy(null);
      }
    },
    [individualId, put]
  );

  const cancelAnalysis = useCallback(
    async (key: string) => {
      setBusy(`${key}:cancel`);
      try {
        await KeyFactsAPI.cancelAnalysis(individualId, key);
        refetch();
        toast.success("Analysis stopped.");
      } catch (error) {
        toast.error(keyFactErrorText(error));
      } finally {
        setBusy(null);
      }
    },
    [individualId, refetch]
  );

  return { busy, act, fileAsExhibit, cancelAnalysis };
};
